import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/index";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { formatCancerDisplay } from "@/components/shared/OncologyContext";
import api from "@/api";
import { toast } from "sonner";
import { Search, Filter, ChevronRight, Clock, UserPlus } from "lucide-react";
import { getItems } from "@/utils/apiHelpers";
import { CreatePatientModal } from "@/components/admin/modals/ClinicAdminModals";

export default function PatientList() {
  const { t } = useLanguage();
  const [patients, setPatients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showCreate, setShowCreate] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [secretaries, setSecretaries] = useState([]);

  useEffect(() => {
    fetchPatients();
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await api.get("/users/clinic-members").catch(() => ({ data: { items: [] } }));
      const list = getItems(res.data);
      setDoctors(list.filter(u => u.role === "MEDECIN"));
      setNurses(list.filter(u => u.role === "IDE"));
      setSecretaries(list.filter(u => u.role === "SECRETAIRE_MEDICAL"));
    } catch {}
  };

  const fetchPatients = async () => {
    try {
      const res = await api.get("/patients?limit=500");
      const patientsList = getItems(res.data);
      const statusOrder = { "CRITICAL": 0, "OPEN_ALERT": 1, "TO_MONITOR": 2, "STABLE": 3, "HOSPITALIZED": 4 };
      const sorted = patientsList.sort((a, b) => {
        const orderA = statusOrder[a.status] ?? 4;
        const orderB = statusOrder[b.status] ?? 4;
        if (orderA !== orderB) return orderA - orderB;
        // Secondary sort by name
        return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
      });
      setPatients(sorted);
      setFiltered(sorted);
    } catch {
      toast.error("Erreur lors du chargement des patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let result = [...patients];

    if (statusFilter !== "ALL") {
      result = result.filter(p => p.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
        p.phone?.includes(q) ||
        (p.pathology || p.cancerType)?.toLowerCase().includes(q)
      );
    }

    const statusOrder = { "CRITICAL": 0, "OPEN_ALERT": 1, "TO_MONITOR": 2, "STABLE": 3, "HOSPITALIZED": 4 };
    result.sort((a, b) => {
      const orderA = statusOrder[a.status] ?? 4;
      const orderB = statusOrder[b.status] ?? 4;
      if (orderA !== orderB) return orderA - orderB;
      return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    });

    setFiltered(result);
  }, [search, statusFilter, patients]);

  const statusOptions = ["ALL", "CRITICAL", "TO_MONITOR", "STABLE"];

  return (
    <ProfessionalLayout title={t("nurse.patientList")}>
      <div className="flex items-center justify-between mb-4">
        <div></div>
        <button
          data-testid="nurse-create-patient-btn"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
        >
          <UserPlus className="w-4 h-4" /> Créer un patient
        </button>
      </div>
      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6 animate-fade-in-up">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un patient..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="patient-search"
            className="w-full h-10 pl-9 pr-4 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {statusOptions.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s.toLowerCase()}`}
              className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === s
                  ? "bg-emerald-600 text-white"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {s === "ALL" ? "Tous" : t(`status.${s}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <p className="text-sm text-slate-500 mb-4 animate-fade-in-up">
        {filtered.length} patient{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}
      </p>

      {/* Patient List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <p>Aucun patient trouvé</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p, idx) => (
            <Link
              key={p.id}
              to={`/nurse/patients/${p.id}`}
              data-testid={`patient-list-item-${p.id}`}
              className={`flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all animate-fade-in-up`}
              style={{ animationDelay: `${idx * 0.03}s` }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-semibold text-sm flex-shrink-0">
                  {p.firstName?.[0]}{p.lastName?.[0]}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-800 text-sm">
                      {p.firstName} {p.lastName}
                    </p>
                  </div>
                  <p className="text-xs text-slate-500 truncate max-w-[180px]">{formatCancerDisplay(p)}</p>
                  {p.lastCheckinAt && (
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      Check-in: {new Date(p.lastCheckinAt).toLocaleDateString("fr-FR")}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={p.currentStatus || p.canonicalStatus || p.status || "STABLE"} size="sm" testId={`nurse-patient-list-current-status-${p.id}`} />
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <CreatePatientModal
          doctors={doctors}
          nurses={nurses}
          secretaries={secretaries}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchPatients(); }}
        />
      )}
    </ProfessionalLayout>
  );
}
